import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Upload,
    Activity,
    FileText,
    PenTool,
    Settings,
    Moon,
    Sun,
    LogOut,
    BookOpen,
    Target,
    BarChart3,
    LineChart,
    AlertTriangle,
    TrendingDown
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import './Layout.css';

interface LayoutProps {
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const location = useLocation();
    const { session, signOut } = useAuth();
    const user = session?.user;
    const userMenuRef = useRef<HTMLDivElement>(null);
    const sidebarTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const navigation = [
        { name: 'アップロード', href: '/upload', icon: Upload },
        { name: '処理ジョブ', href: '/job-list', icon: Activity },
        { name: '結果', href: '/results', icon: FileText },
        { name: '評価レポート', href: '/accuracy', icon: Target },
        { name: '精度モニタリング', href: '/accuracy/monitoring', icon: LineChart },
        { name: 'ページステータス', href: '/status-dashboard', icon: AlertTriangle },
        { name: '違反件数推移', href: '/violation-trend', icon: TrendingDown },
        { name: 'LLM使用状況', href: '/llm-usage', icon: BarChart3 },
        { name: 'ガイドライン', href: '/guidelines', icon: BookOpen },
        { name: '設定', href: '/settings', icon: Settings },
    ];

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
        document.documentElement.setAttribute('data-theme', !isDarkMode ? 'dark' : 'light');
    };


    const handleSidebarMouseEnter = () => {
        if (sidebarTimeoutRef.current) {
            clearTimeout(sidebarTimeoutRef.current);
            sidebarTimeoutRef.current = null;
        }
        setIsSidebarExpanded(true);
    };

    const handleSidebarMouseLeave = () => {
        sidebarTimeoutRef.current = setTimeout(() => {
            setIsSidebarExpanded(false);
        }, 300); // 300ms delay before collapsing
    };

    const isActive = (href: string) => {
        if (href === '/') {
            return location.pathname === '/';
        }
        if (!location.pathname.startsWith(href)) {
            return false;
        }
        // Nested routes (e.g. /accuracy/monitoring) share a prefix with a
        // parent nav item (/accuracy) — only the most specific match should
        // be highlighted.
        const bestMatch = navigation
            .map((item) => item.href)
            .filter((navHref) => navHref !== '/' && location.pathname.startsWith(navHref))
            .sort((a, b) => b.length - a.length)[0];
        return href === bestMatch;
    };

    const handleSignOut = async () => {
        console.log('Sign out button clicked');
        try {
            setIsSigningOut(true);
            console.log('Starting sign out process...');
            await signOut();
            console.log('Sign out completed successfully');
            setShowUserMenu(false);
            // The ProtectedRoute will automatically redirect to login page
        } catch (error) {
            console.error('Sign out error:', error);
            // Even if sign out fails, close the menu
            setShowUserMenu(false);
        } finally {
            setIsSigningOut(false);
        }
    };

    const getUserDisplayName = () => {
        if (!user) return 'ユーザー';

        // Try to get name from different sources
        if (user.name) return user.name;
        if (user.email) return user.email.split('@')[0];
        if (user.username) return user.username;

        return 'ユーザー';
    };

    const getUserInitials = () => {
        if (!user) return 'U';

        const name = getUserDisplayName();
        if (name === 'ユーザー') return 'U';

        // Get first character of each word
        const words = name.split(' ');
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        return name[0].toUpperCase();
    };

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };

        if (showUserMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserMenu]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (sidebarTimeoutRef.current) {
                clearTimeout(sidebarTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="layout">
            {/* Sidebar */}
            <aside
                className={`sidebar ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}
                onMouseEnter={handleSidebarMouseEnter}
                onMouseLeave={handleSidebarMouseLeave}
            >
                <div className="sidebar-header">
                    <div className="logo">
                        <PenTool className="logo-icon" />
                        <span className="logo-text">AIサイトパトローラー</span>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {navigation.map((item) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`nav-item ${isActive(item.href) ? 'nav-item-active' : ''}`}
                            >
                                <Icon size={20} />
                                <span>{item.name}</span>
                            </Link>
                        );
                    })}
                </nav>

                <div className="sidebar-footer">
                    <button
                        className="theme-toggle"
                        onClick={toggleDarkMode}
                        title={`${isDarkMode ? 'ライト' : 'ダーク'}モードに切り替え`}
                    >
                        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                        <span>{isDarkMode ? 'ライト' : 'ダーク'}モード</span>
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <main className="main-content">
                {/* Top bar */}
                <header className="top-bar">
                    <div className="top-bar-left">
                    </div>

                    <div className="top-bar-right">
                        <div className="user-menu-container" ref={userMenuRef}>
                            <div className="user-info" onClick={() => {
                                console.log('User info clicked, current showUserMenu:', showUserMenu);
                                setShowUserMenu(!showUserMenu);
                            }}>
                                <div className="user-avatar">
                                    <span>{getUserInitials()}</span>
                                </div>
                                <span className="user-name">{getUserDisplayName()}</span>
                            </div>

                            {showUserMenu && (
                                <div className="user-menu">
                                    <div className="user-menu-header">
                                        <div className="user-menu-avatar">
                                            <span>{getUserInitials()}</span>
                                        </div>
                                        <div className="user-menu-info">
                                            <div className="user-menu-name">{getUserDisplayName()}</div>
                                            {user?.email && (
                                                <div className="user-menu-email">{user.email}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="user-menu-divider"></div>
                                    <button
                                        className="user-menu-item"
                                        onClick={handleSignOut}
                                        disabled={isSigningOut}
                                    >
                                        <LogOut size={16} />
                                        <span>{isSigningOut ? 'サインアウト中...' : 'サインアウト'}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <div className="page-content">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
